import {last, sort} from "../collections";
import {paddingLeft} from "../strings";
import {monthLabelsFull} from "@Common/utils/date/month-labels-full";
import {monthLabelsShort} from "@Common/utils/date/month-labels-short";
import {chain} from "@Common/utils/fs";

export const parseDateFormat = (pattern) => {
    const tokens = toTokens(pattern);
    return {
        pattern,
        formatDate: (date) => {
            return tokens.map((t) => {
                if (t.type === "literal") {
                    return t.str;
                } else {
                    return t.token.format(date);
                }
            }).join("");
        },
        parseDate: (str) : any => {
            const litPoses = pinLiterals(str, tokens);
            if (!litPoses) {
                return null;
            }
            const hydrated = hydrateTokens(litPoses, {tokens, str});
            if (!hydrated) {
                return null;
            }
            let ret = {};
            try {
                hydrated.forEach((h) => ret = h.token.parsing.parse(h.str)(ret));
                return ret;
            } catch (e) {
                return null;
            }
        },
    };
};

const hydrateTokens = (litPoses, {tokens, str}) => {
    let index = 0;
    let ret = [];

    const checkStr = (s, token) => {
        const minLength = token.parsing.minLength ?? token.placeholder.length;
        if (s.length < minLength) {
            return false;
        }
        const maxLength = token.parsing.maxLength ?? token.placeholder.length;
        if (s.length > maxLength) {
            return false;
        }
        if (token.parsing.condition && !token.parsing.condition(s)) {
            return false;
        }
        return true;
    };

    for (const litPose of litPoses) {
        const s = str.substring(index, litPose.strIndex);

        const token = tokens[litPose.tokenIndex - 1].token;
        if (!checkStr(s, token)) {
            return null;
        }
        ret.push({
            str: s,
            token,
        });
        index = litPose.strIndex + tokens[litPose.tokenIndex].str.length;
    }
    if (index < str.length) {
        const s = str.substring(index);
        const token = last(tokens).token;

        if (!checkStr(s, token)) {
            return null;
        }

        ret.push({
            str: s,
            token,
        });
    }
    if (ret.length < tokens.filter((t) => t.type === "token").length) {
        return null;
    }
    return ret;
};

const pinLiterals = (str, tokens) => {
    let strIndex = 0;
    let ret = [];
    for (let tokenIndex = 0; tokenIndex < tokens.length; tokenIndex++) {
        const token = tokens[tokenIndex];
        if (token.type !== "literal") {
            continue;
        }
        const index = str.indexOf(token.str, strIndex);
        if (index === -1) {
            return null;
        }
        strIndex = index + token.str.length;
        ret.push({
            tokenIndex,
            strIndex: index,
        });
    }
    return ret;
};

const formatTokens = chain(
    [
        {
            placeholder: "yyyy",
            format: (d) => d.year,
            parsing: {
                parse: (str) => (d) => ({...d, year: +str}),
                condition: (str) => !isNaN(str),
            },
        },
        {
            placeholder: "yy",
            format: (d) => d.year.toString().substring(2),
            parsing: {
                parse: (str) => (d) => ({...d, year: 2000 + +str}),
                condition: (str) => !isNaN(str),
            },
        },
        {
            placeholder: "mm",
            format: (d) => paddingLeft(d.month),
            parsing: {
                parse: (str) => (d) => ({...d, month: +str}),
                minLength: 1,
                condition: (str) => !isNaN(str),
            },
        },
        {
            placeholder: "MMM",
            format: (d) => monthLabelsShort[d.month - 1],
            // No parsing support for this token now
        },
        {
            placeholder: "MMMM",
            format: (d) => monthLabelsFull[d.month - 1],
            // No parsing support for this token now
        },
        {
            placeholder: "dd",
            format: (d) => paddingLeft(d.day),
            parsing: {
                parse: (str) => (d) => ({...d, day: +str}),
                minLength: 1,
                condition: (str) => !isNaN(str),
            },
        },
    ],
    (_) => sort(_, ({placeholder}) => -placeholder.length),
);

const toTokens = (format) => {
    let index = 0;
    let ret = [];

    for (let i = index; i < format.length; i++) {
        const ft = formatTokens.find((ft) => format.startsWith(ft.placeholder, i));
        if (ft) {
            if (i > index) {
                ret.push({
                    type: "literal",
                    str: format.substring(index, i),
                });
            }
            ret.push({
                type: "token",
                token: ft,
            });
            index = i + ft.placeholder.length;
            i = index - 1;
        }
    }
    if (index < format.length) {
        ret.push({
            type: "literal",
            str: format.substring(index),
        });
    }
    return ret;
};
